import { useMemo } from 'react';
import { useSettings } from './useSettings';

export interface MonthOption {
  value: string;
  label: string;
}

function toMonthStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Returns month options clamped to the app launch month (from system settings).
 * @param locale  — e.g. 'he-IL' or 'en-US'
 * @param futureMonths — how many months ahead of today to include (default 0)
 */
export function useMonthOptions(locale: string, futureMonths = 0): MonthOption[] {
  const { data: settings } = useSettings();
  const launchMonth = settings?.appLaunchMonth || '2026-03';

  return useMemo(() => {
    const [launchYear, launchMon] = launchMonth.split('-').map(Number);
    const launchDate = new Date(launchYear, launchMon - 1, 1);
    const now = new Date();
    const options: MonthOption[] = [];

    // Start from futureMonths ahead, go backwards until launch
    for (let i = -futureMonths; ; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      if (d < launchDate) break;
      options.push({
        value: toMonthStr(d),
        label: d.toLocaleDateString(locale, { month: 'long', year: 'numeric' }),
      });
    }

    return options;
  }, [locale, launchMonth, futureMonths]);
}
